import { sesTemplateJson, sesColumnsJson } from './static';
import { fetchFun } from '../../../utils/fetchFun';

// Groups each JE's raw debit/credit lines into a display block. 1-1 and S-M/M-S entries keep
// their auto-paired splitedRows (one block per pair, like CFS); true M-M entries render all
// debit lines then all credit lines as one unclassified block (CFS's manual-split case) unless
// the user has already manually split that journal entry (mmmSplitEntries, keyed by the QB
// journal number - see SESSplitMultiMultiModal), in which case each manual pairing becomes its
// own ordinary block so it flows through the same classification as any other entry.
// Shared by SESWorksheet.jsx (interactive view) and buildSesStatementBody below (statement
// generation), so the two can never classify the same journal data differently.
export const buildEntryBlocks = (periodData, mmmSplitEntries = []) => {
    if (!periodData?.length) return { blocks: [], mmmCount: 0 };
    const savedSplitByJournalId = new Map((mmmSplitEntries || []).map(e => [e.journalId, e]));
    const blocks = [];
    let mmmCount = 0;
    periodData.forEach((entry, entryIndex) => {
        const isTrueMMM = !entry?.splitedRows?.length && entry?.debitRows?.length > 1 && entry?.creditRows?.length > 1;
        const savedSplit = isTrueMMM ? savedSplitByJournalId.get(entry.number) : null;

        if (entry?.splitedRows?.length) {
            entry.splitedRows.forEach((pair, pairIndex) => {
                blocks.push({
                    key: `${entryIndex}-${pairIndex}`,
                    number: entry.number,
                    date: entry.date,
                    debitLines: [pair[0]],
                    creditLines: [pair[1]],
                    isMMM: false,
                });
            });
        } else if (savedSplit?.isSplited && savedSplit?.manualSplitData?.splitedData?.length) {
            const { splitBy, splitedData } = savedSplit.manualSplitData;
            splitedData.forEach((pairing, pairingIndex) => {
                if (splitBy === "byDebits") {
                    (pairing.creditRows || []).forEach((creditRow, ci) => {
                        blocks.push({
                            key: `${entryIndex}-mm-${pairingIndex}-${ci}`,
                            number: entry.number,
                            date: entry.date,
                            debitLines: [{ ...pairing.debitRow, debit: creditRow.dividedAmount, credit: "" }],
                            creditLines: [{ ...creditRow, credit: creditRow.dividedAmount, debit: "" }],
                            isMMM: false,
                        });
                    });
                } else {
                    (pairing.debitRows || []).forEach((debitRow, di) => {
                        blocks.push({
                            key: `${entryIndex}-mm-${pairingIndex}-${di}`,
                            number: entry.number,
                            date: entry.date,
                            debitLines: [{ ...debitRow, debit: debitRow.dividedAmount, credit: "" }],
                            creditLines: [{ ...pairing.creditRow, credit: debitRow.dividedAmount, debit: "" }],
                            isMMM: false,
                        });
                    });
                }
            });
        } else if (isTrueMMM) {
            mmmCount++;
            blocks.push({
                key: `mmm-${entryIndex}`,
                number: entry.number,
                date: entry.date,
                debitLines: entry.debitRows,
                creditLines: entry.creditRows,
                isMMM: true,
                journalId: entry.number,
                index: entryIndex,
            });
        }
    });
    return { blocks, mmmCount };
};

// O(1) library lookup keyed by the exact debit+credit account-name pair - same matching
// strategy SESWorksheet.jsx's interactive view uses (sesLibraryIndex/findLibraryMatch).
export const buildSesLibraryIndex = (sesLibrary) => {
    const map = new Map();
    (sesLibrary || []).forEach(item => {
        const key = `${item.debitCaption?.trim().toLowerCase()}__${item.creditCaption?.trim().toLowerCase()}`;
        map.set(key, item);
    });
    return map;
};

// Maps a raw GL account name to its TB-GRP grouped "row" caption (e.g. "Citi Bank" -> "Cash
// and cash equivalents"), from the same PeriodGroupedData.periodgroupedData the SES-WP
// Beginning Balances fetch already reads (sesWp.controller.js's getSesBeginningBalances).
// Already fetched centrally by DefaultLayout (fetchGroupings -> state.groupingSlice.groupings),
// so no extra network round-trip is needed for this.
export const buildAccountRowIndex = (periodgroupedData) => {
    const map = new Map();
    // Keyed by accountName - PeriodGroupedData rows have no `title` field (see backend
    // ses.service.js#buildAccountRowIndexV1); `title` kept only as a fallback for older data.
    (periodgroupedData || []).forEach(item => {
        const name = item?.accountName || item?.title;
        if (name && item?.row) {
            map.set(name.trim().toLowerCase(), item.row.trim().toLowerCase());
        }
    });
    return map;
};

// Step-wise matching (spec 1.03: "SES Library - Step-wise Matching sequence... Same as in
// CFS"): try the exact raw account-name pair first, then fall back to each account's TB-GRP
// grouped row - a library rule written against a grouped caption like "Cash and cash
// equivalents" should still match a raw GL account named "Citi Bank" that TB-GRP groups under
// that caption, not only an account literally named "Cash and cash equivalents". A block only
// counts toward the statement when one of these two levels finds a real library match - manual
// picks and unresolved M-M entries are excluded, same "unclassified" definition the Workpaper
// page itself uses (manual edits never make a block "matched", only a saved library row does).
// Shared by SESWorksheet.jsx's interactive view and buildSesStatementBody below, so the two can
// never classify the same journal data differently.
export const classifyBlock = (block, sesLibraryIndex, accountRowIndex = new Map()) => {
    if (block.isMMM) return { matched: false };
    const debitName = block.debitLines[0]?.accountName?.trim().toLowerCase();
    const creditName = block.creditLines[0]?.accountName?.trim().toLowerCase();

    let match = sesLibraryIndex.get(`${debitName}__${creditName}`);

    if (!match) {
        const debitRow = accountRowIndex.get(debitName);
        const creditRow = accountRowIndex.get(creditName);
        if (debitRow || creditRow) {
            match = sesLibraryIndex.get(`${debitRow || debitName}__${creditRow || creditName}`);
        }
    }

    if (!match) return { matched: false };
    return {
        caption: match.toGroup?.[0]?.caption,
        column: match.toGroup?.[0]?.column,
        method: match.toGroup?.[0]?.method,
        matched: true,
    };
};

// Net Income isn't a workpaper-classified JE (it's the period's Income Statement result
// rolling into Retained Earnings), so it can't come from the library/journal classification
// above - it has to be read off the already-generated Income Statement. The row's title varies
// by entity ("Net income", "Net (loss)", or "Net income (loss)" - see IncomeStatement.jsx/
// CFSIndirect.jsx's own netIncomeitles list), so match on all three rather than one literal string.
const NET_INCOME_TITLES = ["Net income", "Net (loss)", "Net income (loss)"];
export const extractNetIncome = (incomeStatementData, periodIndex) => {
    const row = incomeStatementData?.body?.find(item => NET_INCOME_TITLES.includes(item?.title));
    const balance = row?.balance;
    if (Array.isArray(balance)) return Number(balance[periodIndex]) || 0;
    return Number(balance) || 0;
};

// Turns one period's classified Workpaper entries into an SES Statement body: sums each
// matched block's signed amount into its (Caption, Column) bucket, adds Net Income (Retained
// Earnings only), then adds each column's beginning balance to get an ending balance.
export const buildSesStatementBody = ({ periodJournalData, sesLibraryIndex, accountRowIndex, beginningBalances, beginningDataKey, netIncome = 0, mmmSplitEntries = [] }) => {
    const { blocks } = buildEntryBlocks(periodJournalData, mmmSplitEntries);
    if (!blocks.length) return null;

    const buckets = {};
    let unclassifiedCount = 0;
    blocks.forEach(block => {
        const cls = classifyBlock(block, sesLibraryIndex, accountRowIndex);
        if (!cls.matched) {
            unclassifiedCount++;
            return;
        }
        const amount = Number(block.debitLines[0]?.debit || block.creditLines[0]?.credit || 0);
        const signed = cls.method === "negative" ? -amount : amount;
        const key = `${cls.caption}__${cls.column}`;
        buckets[key] = (buckets[key] || 0) + signed;
    });

    const beginningByColumn = {};
    sesColumnsJson.forEach(col => {
        beginningByColumn[col.title] = beginningBalances?.[col.title]?.[beginningDataKey]?.total || 0;
    });

    const body = [{
        title: "Beginning balance",
        isTotalRow: false,
        amounts: { ...beginningByColumn },
        total: Object.values(beginningByColumn).reduce((a, b) => a + b, 0),
    }];

    sesTemplateJson.forEach(caption => {
        const amounts = {};
        sesColumnsJson.forEach(col => {
            amounts[col.title] = buckets[`${caption.title}__${col.title}`] || 0;
        });
        body.push({
            title: caption.title,
            isTotalRow: false,
            amounts,
            total: Object.values(amounts).reduce((a, b) => a + b, 0),
        });
    });

    {
        const amounts = {};
        sesColumnsJson.forEach(col => {
            amounts[col.title] = col.title === "Retained earnings" ? netIncome : 0;
        });
        body.push({
            title: "Net income",
            isTotalRow: false,
            amounts,
            total: netIncome,
        });
    }

    const endingByColumn = {};
    sesColumnsJson.forEach(col => {
        const netChange = body.slice(1).reduce((sum, row) => sum + (row.amounts[col.title] || 0), 0);
        endingByColumn[col.title] = beginningByColumn[col.title] + netChange;
    });
    body.push({
        title: "Ending balance",
        isTotalRow: true,
        amounts: endingByColumn,
        total: Object.values(endingByColumn).reduce((a, b) => a + b, 0),
    });

    return { columns: sesColumnsJson.map(c => c.title), body, unclassifiedCount };
};

// Computes + saves the SES Statement for one period. Both the Workpaper's "Generate Statement"
// button and the auto-regenerate thunk call this, so they can never drift from each other.
export const generateAndSaveSesStatement = async ({ periodKey, periodType, sesJournalData, sesLibraryIndex, accountRowIndexes, sesBeginningBalances, incomeStatementData, mmmSplitEntries, clientId, customerId, userId }) => {
    if (!periodType) return null;
    const beginningDataKey = periodKey === "periodOne" ? "periodOneData" : "periodTwoData";
    const netIncome = extractNetIncome(incomeStatementData, periodKey === "periodOne" ? 0 : 1);
    const result = buildSesStatementBody({
        periodJournalData: sesJournalData?.[periodKey],
        sesLibraryIndex,
        accountRowIndex: accountRowIndexes?.[periodKey],
        beginningBalances: sesBeginningBalances,
        beginningDataKey,
        netIncome,
        mmmSplitEntries: mmmSplitEntries?.[periodKey === "periodOne" ? "periodOneData" : "periodTwoData"] || [],
    });
    if (!result) return null;

    const raw = JSON.stringify({
        clientId,
        customerId,
        userId,
        periodType,
        columns: result.columns,
        body: result.body,
        unclassifiedCount: result.unclassifiedCount,
    });
    return fetchFun(`/api/v1/equityholder-statement/statement`, "POST", raw);
};
